import {
	Component,
	EventEmitter,
	Input,
	OnDestroy,
	Output,
	ViewChild,
	ElementRef,
	ChangeDetectorRef,
} from '@angular/core';
import { MediaDepotAsset, NamedBlob } from '@mira-avant/mtlibs';
import { NumberInput } from 'ng-zorro-antd/core/types';
import { Subscription } from 'rxjs';
import { FileHelper } from '../../../utils/file.helper';
import { DragHelper } from '../../../utils/drag.helper';
import { AngularModule } from '../../../modules/angular.module';
import { ZorroModule } from '../../../modules/zorro.module';
import { MetabiUploaderService, UploadResult, UploadState } from '../../../services/metabi-uploader.service';
import { v4 } from 'uuid';

@Component({
	selector: 'app-upload',
	standalone: true,
	imports: [AngularModule, ZorroModule],
	templateUrl: './upload.component.html',
	styleUrl: './upload.component.scss',
})
export class UploadComponent implements OnDestroy {
	public isHovering: boolean = false;
	public isUploading: boolean = false;
	public progress: number = 0;

	@Input() small: boolean = false;
	@Input() title: string = 'Click or drag file to upload';
	@Input() accept: string = '.zip,.h5p,application/zip,application/x-zip-compressed,application/h5p';

	@Input() directory: boolean = false;
	@Input() multiple: boolean = false;
	@Input() pick: string = 'files'; // or "directory"
	@Input('upload-id') uploadId: string = 'upload-' + v4();

	@Output() onCompleted: EventEmitter<UploadResult> = new EventEmitter<UploadResult>();

	@ViewChild('uploadInput', { static: true }) uploadInput!: ElementRef<HTMLInputElement>;

	private uploadSubscription: Subscription | undefined;

	constructor(
		private metabiUploadService: MetabiUploaderService,
		private cdr: ChangeDetectorRef
	) {}

	ngOnDestroy() {
		this.unsubscribe();
	}

	public get percent(): NumberInput {
		return this.progress;
	}

	public onDrag(event: DragEvent): void {
		this.isHovering = event.type == 'dragenter' && !this.isUploading;
	}

	public onDrop(event: DragEvent): void {
		event.preventDefault();
		this.isHovering = false;
		DragHelper.getDroppedFiles$(event).subscribe(
			(files: any[]) => {
				const fileList = {
					length: files.length,
					item: (index: number) => files[index],
					[Symbol.iterator]: function* () {
						yield* files;
					},
				} as FileList;
				this.handleFileSelection(fileList);
			},
			error => this.handleError(error)
		);
	}

	public onClick(event: Event): void {
		this.setDirectoryUpload(this.pick === 'directory');
	}

	public onChange(event: Event): void {
		const input = event.target as HTMLInputElement;
		if (input.files) {
			this.handleFileSelection(input.files);
		}
	}

	private handleFileSelection(files: FileList): void {
		const allowedFiles = this.getAllowedFiles(files);
		if (allowedFiles.length) {
			this.startUpload(allowedFiles);
		} else if (this.accept) {
			this.handleError(new Error(`Please select an allowed file type: ${this.allowedFileTypes}`));
		}
	}

	private startUpload(files: (File | NamedBlob)[]): void {
		this.isUploading = true;
		this.progress = 0;
		this.cdr.detectChanges();
		this.uploadFiles(files);
	}

	private uploadFiles(files: (File | NamedBlob)[]): void {
		files.forEach((file: File | NamedBlob) => {
			this.uploadSubscription = this.metabiUploadService.uploadFile(file as File).subscribe({
				next: ({ progress, result }) => {
					this.progress = progress.progress;
					this.isUploading = progress.state === UploadState.IN_PROGRESS;
					this.cdr.detectChanges();

					if (result) {
						this.handleUploadComplete(true, 'Upload Completed', result);
					}
				},
				error: error => this.handleError(error),
			});
		});
	}

	private getAllowedFiles(fileList: FileList | File[] | NamedBlob[]): (File | NamedBlob)[] {
		const files = fileList instanceof FileList ? Array.from(fileList) : fileList;

		if (this.accept) {
			const acceptedTypes: string[] = this.accept.split(',').map(type => type.trim());

			const acceptedFiles: (File | NamedBlob)[] = [];
			const rejectedFiles: (File | NamedBlob)[] = [];

			for (const file of files) {
				if (file.name.startsWith('.')) {
					rejectedFiles.push(file);
					continue;
				}

				const fileExtension = '.' + file.name.split('.').pop()!.toLowerCase();
				const fileMimeType = file.type.toLowerCase();
				const isAccepted = acceptedTypes.some(type => {
					if (type.startsWith('.')) {
						return type.toLowerCase() === fileExtension;
					} else {
						const [acceptedType, acceptedSubtype] = type.split('/');
						const [fileType, fileSubtype] = fileMimeType.split('/');
						return (
							acceptedType === fileType && (acceptedSubtype === '*' || acceptedSubtype === fileSubtype)
						);
					}
				});

				if (isAccepted) {
					acceptedFiles.push(file);
				} else {
					rejectedFiles.push(file);
				}
			}

			if (rejectedFiles.length > 0) {
				this.showRejectedFilesAlert(rejectedFiles);
			}
			return acceptedFiles;
		} else {
			return files;
		}
	}

	private unsubscribe(): void {
		if (this.uploadSubscription) {
			this.uploadSubscription.unsubscribe();
			this.uploadSubscription = undefined;
		}
	}

	private handleError(error: Error): void {
		this.handleUploadComplete(false, error.message);
		this.isUploading = false;
		this.progress = 0;
		this.cdr.detectChanges();
	}

	private handleUploadComplete(
		success: boolean,
		message: string,
		result?: MediaDepotAsset | MediaDepotAsset[]
	): void {
		this.onCompleted.emit({
			progress: {
				progress: success ? 100 : this.progress,
				state: success ? UploadState.DONE : UploadState.PENDING,
			},
			result,
		});
		if (success) {
			this.reset();
		}
	}

	private reset(): void {
		this.isUploading = false;
		this.progress = 0;
		if (this.uploadInput.nativeElement) {
			this.uploadInput.nativeElement.value = '';
		}
		this.cdr.detectChanges();
	}

	private setDirectoryUpload(bool: boolean = this.directory && this.multiple): void {
		if (bool) {
			this.uploadInput.nativeElement.setAttribute('webkitdirectory', 'true');
		} else {
			this.uploadInput.nativeElement.removeAttribute('webkitdirectory');
		}
	}

	private showRejectedFilesAlert(rejectedFiles: (File | NamedBlob)[]): void {
		const inferredFileExtension: string = this.accept
			.split(',')
			.map(type => FileHelper.getMimetypeExt(type.trim()).toUpperCase())
			.filter(ext => ext !== '')
			.join(' or ');
		const messagePart1: string = `The following file${rejectedFiles.length !== 1 ? 's' : ''} could not be uploaded:`;
		const messagePart2: string = rejectedFiles.map(file => file.name).join('<br />');
		const messagePart3: string = inferredFileExtension
			? `Please ensure your file type is ${inferredFileExtension} and try again.`
			: `File must have mimeType ${this.accept}:`;
		alert(`Incorrect File Format. ${messagePart1} ${messagePart2} ${messagePart3}`);
	}

	public get allowedFileTypes(): string {
		return this.accept
			.split(',')
			.map(type => {
				let segments = type.split('/');
				return segments[1] && segments[1] != '*' ? '.' + segments[1] : segments[0];
			})
			.join(', ');
	}
}
